import axios from "axios";

class HttpService {
  constructor(baseURL) {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_BACKEND_URL || "http://localhost:3000",
      withCredentials: true,
     });

    // Flag para evitar múltiplas tentativas de refresh simultâneas
    this.isRefreshing = false;
    // Fila de requisições que falharam enquanto refresh estava em andamento
    this.failedQueue = [];

    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response) {
          // Token expirado - tentar renovar automaticamente
          if (error.response.status === 401 && !originalRequest._retry) {
            // Evitar loop infinito
            if (originalRequest.url?.includes("/auth/refresh")) {
              // Se o próprio refresh falhou, fazer logout
              this.handleLogout();
              return Promise.reject(error);
            }

            originalRequest._retry = true;

            // Se já está renovando, adicionar à fila
            if (this.isRefreshing) {
              return new Promise((resolve, reject) => {
                this.failedQueue.push({ resolve, reject });
              })
                .then((token) => {
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  return this.api(originalRequest);
                })
                .catch((err) => {
                  return Promise.reject(err);
                });
            }

            this.isRefreshing = true;

            const refreshToken = localStorage.getItem("refresh_token");

            if (!refreshToken) {
              this.handleLogout();
              return Promise.reject(error);
            }

            try {
              // Tentar renovar o token
              const response = await this.api.post("/auth/refresh", {
                refresh_token: refreshToken,
              });

              const { access_token, refresh_token: newRefreshToken } =
                response.data;

              // Armazenar novos tokens
              localStorage.setItem("token", access_token);
              if (newRefreshToken) {
                localStorage.setItem("refresh_token", newRefreshToken);
              }

              // Processar fila de requisições que falharam
              this.processQueue(null, access_token);

              // Retentar a requisição original com novo token
              originalRequest.headers.Authorization = `Bearer ${access_token}`;
              return this.api(originalRequest);
            } catch (refreshError) {
              // Refresh falhou - fazer logout
              this.processQueue(refreshError, null);
              this.handleLogout();
              return Promise.reject(refreshError);
            } finally {
              this.isRefreshing = false;
            }
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Processa a fila de requisições que falharam durante refresh
   */
  processQueue(error, token = null) {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });

    this.failedQueue = [];
  }

  /**
   * Limpa tokens e redireciona para login
   */
  handleLogout() {
    localStorage.removeItem("token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    
    // Evitar redirect em loop se já estiver na página de login
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }

  get(url, config = {}) {
    return this.api.get(url, config);
  }

  post(url, data, config = {}) {
    return this.api.post(url, data, config);
  }

  put(url, data, config = {}) {
    return this.api.put(url, data, config);
  }

  delete(url, config = {}) {
    return this.api.delete(url, config);
  }
}

export default HttpService;
